<div class="content-block light-control-host usage-billing">
  <!-- Sticky header: page title, plan identity, and the dates that govern billing.
       The contract facts are entitlement-sourced and guarded separately from the
       dashboard, so they still answer "what do I have, and until when?" when the
       usage endpoints fail. -->
  <div class="header-container" #headerContainer>
    <div class="header-top">
      <div class="title-row">
        <h1>Usage &amp; Billing</h1>
        <span class="title-badge" *ngIf="entitlementLoaded" data-cy="product-edition">{{ productEdition }}</span>
        <span class="title-badge accent" *ngIf="isFlexBilling" data-cy="billing-model">Flex billing</span>
        <!-- Sits with the title rather than the contract dates: the definition governs every
             billable figure on the page, and the sticky header keeps it one click away no
             matter how far the dashboard is scrolled. -->
        <a
          class="help-link"
          target="_blank"
          rel="noopener"
          [href]="uiService.billableResourcesDocsUrl"
          data-cy="billable-resources-help-link"
        >
          <svg lucideCircleQuestionMark aria-hidden="true"></svg>
          What’s billable?
        </a>
      </div>
      <div class="header-meta">
        <p class="subtitle" *ngIf="vm" data-cy="billing-cycle">{{ billingCycleSubtitle }}</p>
        <p class="subtitle contract-meta" *ngIf="entitlementLoaded" data-cy="contract-details">
          <span [class.expired]="subscriptionExpired" data-cy="subscription-expiration">
            {{ subscriptionExpired ? 'Subscription expired' : 'Subscription expires' }}
            {{ subscriptionExpiration | date: 'mediumDate' }}
          </span>
        </p>
      </div>
    </div>
  </div>

  <!-- Loading -->
  <div class="loading-state-content" *ngIf="loading">
    <svg lucideLoaderCircle class="lucide-spin"></svg>
    <p class="loading-state-message">Loading usage &amp; billing…</p>
  </div>

  <!-- Load error (real data only — no sample fallback) -->
  <div class="notice status over" *ngIf="loadError && !loading">
    <svg lucideCircleAlert></svg>
    <span>Couldn’t load usage &amp; billing data. Please try again.</span>
  </div>

  <ng-container *ngIf="vm && !loading">
    <!-- Key metrics: current-billing-period snapshot, independent of the filter below -->
    <section class="stats-section">
      <div class="kpi-row">
        <div class="kpi-card">
          <span class="kpi-label">Contracted resources</span>
          <div class="kpi-figure">
            <span class="kpi-value" data-cy="contracted-resources-value">{{
              (vm.kpis.contracted | number) ?? '—'
            }}</span>
          </div>
          <span class="kpi-sub">contracted cap</span>
        </div>

        <div class="kpi-card">
          <span class="kpi-label">Current month high water mark</span>
          <div class="kpi-figure">
            <span class="kpi-ratio">
              <span class="kpi-value" data-cy="monthly-hwm-value">{{ vm.kpis.monthlyHwm | number }}</span>
              <span class="kpi-denom" data-cy="monthly-hwm-contracted-value"
                >/ {{ (vm.kpis.contracted | number) ?? '—' }}</span
              >
            </span>
            <span
              class="status-pill"
              data-cy="monthly-hwm-status"
              [class.within]="vm.kpis.within"
              [class.over]="!vm.kpis.within"
            >
              @if (vm.kpis.within) {
                <svg lucideCheck></svg>
              } @else {
                <svg lucideCircleAlert></svg>
              }
              {{ vm.kpis.within ? 'Within' : 'Over' }}
            </span>
          </div>
          <span class="kpi-sub" data-cy="monthly-hwm-headroom" [class.over-text]="!vm.kpis.within">
            {{ headroomLabel
            }}<ng-container *ngIf="vm.kpis.monthlyHwmDateLabel"> · hit {{ vm.kpis.monthlyHwmDateLabel }}</ng-container>
          </span>
        </div>

        <div class="kpi-card">
          <span class="kpi-label">Current resource count</span>
          <div class="kpi-figure">
            <span class="kpi-value" data-cy="current-resource-count-value">{{
              (vm.kpis.currentBillable | number) ?? '—'
            }}</span>
          </div>
          <span class="kpi-sub" data-cy="current-resource-as-of">billable · as of {{ vm.kpis.currentAsOfLabel }}</span>
        </div>

        <div class="kpi-card">
          <span class="kpi-label">Applications</span>
          <div class="kpi-figure">
            <span class="kpi-value" data-cy="applications-total-value">{{ vm.kpis.appsTotal | number }}</span>
          </div>
          <span class="kpi-sub" data-cy="applications-cloud-summary"
            >{{ vm.kpis.appsAws | number }} AWS · {{ vm.kpis.appsAzure | number }} Azure</span
          >
        </div>
      </div>
      <span class="stats-caption">
        The high-level metrics above reflect your current billing period ({{ vm.billingCycle.label }}).
        <ng-container *ngIf="noBillableUsage">
          No billable usage has been recorded for this cycle — billable counts come from completed backups.
        </ng-container>
      </span>
    </section>

    <!-- Sticky controls: the dashboard filter + graph tabs pin together below the header -->
    <div class="dashboard-controls" #controlsBar>
      <div class="filter-bar">
        <div class="control-group period-group">
          <span class="filter-label">Period</span>
          <div class="chip-group">
            <button
              *ngFor="let p of periodPresets"
              class="chip"
              [attr.data-cy]="'period-preset-' + p.key"
              [class.active]="periodKey === p.key"
              (click)="setPeriodPreset(p.key)"
            >
              {{ p.label }}
            </button>
          </div>
          <div class="period-range">
            <input
              class="date-input"
              type="date"
              [min]="minDate"
              [max]="maxDate"
              [value]="periodStart"
              (change)="setPeriodStart($event)"
              data-cy="period-start-input"
              aria-label="Period start"
            />
            <span class="date-sep">–</span>
            <input
              class="date-input"
              type="date"
              [min]="minDate"
              [max]="maxDate"
              [value]="periodEnd"
              (change)="setPeriodEnd($event)"
              data-cy="period-end-input"
              aria-label="Period end"
            />
          </div>
        </div>
        <div class="control-group filter-cloud">
          <span class="filter-label">Cloud</span>
          <div class="chip-group">
            <button
              *ngFor="let opt of cloudOptions"
              class="chip"
              [attr.data-cy]="'cloud-filter-' + opt.value"
              [class.active]="cloud === opt.value"
              (click)="setCloud(opt.value)"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>
      </div>

      <div class="tab-strip" role="tablist">
        <button
          class="tab"
          role="tab"
          id="usage-tab-trend"
          data-cy="usage-tab-trend"
          aria-controls="usage-panel-trend"
          [attr.aria-selected]="activeTab === 'trend'"
          [class.active]="activeTab === 'trend'"
          [disabled]="!trendAvailable"
          [attr.aria-disabled]="!trendAvailable"
          [pTooltip]="trendDisabledReason"
          tooltipStyleClass="tooltip-long"
          tooltipPosition="top"
          (click)="setTab('trend')"
        >
          Usage Trend
        </button>
        <button
          class="tab"
          role="tab"
          id="usage-tab-apps"
          data-cy="usage-tab-apps"
          aria-controls="usage-panel-apps"
          [attr.aria-selected]="activeTab === 'apps'"
          [class.active]="activeTab === 'apps'"
          (click)="setTab('apps')"
        >
          Applications
        </button>
      </div>
    </div>

    <!-- Tab content: fixed-height viewport pane that scrolls its own overflow -->
    <div class="tab-panes">
      <!-- Usage Trend tab -->
      <div *ngIf="activeTab === 'trend'" role="tabpanel" id="usage-panel-trend" aria-labelledby="usage-tab-trend">
        <div class="section-header">
          <div>
            <h3>High Water Mark Trend</h3>
            <p class="panel-sub" *ngIf="granularity === 'monthly'">
              Monthly high water mark vs. contracted, {{ periodRangeLabel }}
            </p>
            <p class="panel-sub" *ngIf="granularity === 'daily'">
              Daily billable resources, {{ periodRangeLabel }}
              <span *ngIf="dailyRangeTruncated"> · daily detail starts {{ dailyFloorLabel }}</span>
              <!-- Monthly is empty too when nothing was ever recorded, so the hand-off
                   only applies while the range does hold records. -->
              <span *ngIf="dailyRangeUnavailable && !rangeBeforeUsageData">
                · no daily detail for this range — switch to Monthly</span
              >
            </p>
          </div>
          <div class="panel-controls">
            <div class="control-group">
              <span class="filter-label">View</span>
              <div class="chip-group">
                <button
                  class="chip"
                  data-cy="granularity-daily"
                  [class.active]="granularity === 'daily'"
                  [disabled]="!canViewDaily"
                  [pTooltip]="dailyDisabledReason"
                  tooltipStyleClass="tooltip-long"
                  tooltipPosition="top"
                  (click)="setGranularity('daily')"
                >
                  Daily
                </button>
                <button
                  class="chip"
                  data-cy="granularity-monthly"
                  [class.active]="granularity === 'monthly'"
                  [disabled]="!canViewMonthly"
                  [pTooltip]="monthlyDisabledReason"
                  tooltipStyleClass="tooltip-long"
                  tooltipPosition="top"
                  (click)="setGranularity('monthly')"
                >
                  Monthly
                </button>
              </div>
            </div>
          </div>
        </div>

        <section class="panel">
          <div class="chart-container" data-cy="usage-trend-chart" #trendChart>
            <!-- Overlays the drawn chart rather than replacing it, so the axes don't
                 collapse and reflow while earlier cycles are fetched. -->
            <div class="chart-loading" *ngIf="chartLoading" role="status">
              <svg lucideLoaderCircle class="lucide-spin" aria-hidden="true"></svg>
              <span>Loading daily detail…</span>
            </div>
            <!-- Overlays the empty axes rather than replacing them, matching the loading
                 treatment above, so the panel keeps its shape. -->
            <!-- role="status": the overlay carries the chart's whole meaning once the
                 axes are empty, so it has to be announced rather than seen only. -->
            <div
              class="chart-empty"
              *ngIf="rangeBeforeUsageData && !chartLoading"
              role="status"
              data-cy="chart-no-usage-data"
            >
              <svg lucideInfo aria-hidden="true"></svg>
              <span>{{ noUsageDataMessage }}</span>
            </div>
          </div>

          <div class="chart-legend">
            <span><i class="dot blue"></i> Within contract</span>
            <span><i class="dot amber"></i> Over contract</span>
            <span *ngIf="contractedLineVisible"><i class="dash"></i> Contracted</span>
            <span class="chart-hint">Drag to pan · ⌘/Ctrl + scroll to zoom · double-click to reset</span>
          </div>
        </section>
      </div>

      <!-- Applications tab -->
      <div *ngIf="activeTab === 'apps'" role="tabpanel" id="usage-panel-apps" aria-labelledby="usage-tab-apps">
        <div class="section-header">
          <div>
            <h3>Top 10 Applications</h3>
            <p class="panel-sub">
              Use the filters above to see your high water mark day by application for the selected period.<br />
              <span class="panel-sub-detail">
                Ranked by billable count on {{ breakdownDayLabel }}.
                <span *ngIf="breakdownRangeTruncated">&nbsp;Application detail covers this billing cycle only</span>
              </span>
            </p>
          </div>
          <div class="panel-controls">
            <!-- Condensed from the PRD's approved copy for "Resource Count".
                 "to the same target region" is load-bearing: sharing across different target
                 regions is billed separately. -->
            <p class="fine-print">
              Resources shared across applications to the same target region are billed once, even though the CSV lists
              them per application.
            </p>
            <div class="panel-controls-row">
              <p-iconfield class="app-search">
                <input
                  pInputText
                  #appSearchInput
                  type="text"
                  class="input-small"
                  data-cy="usage-application-search"
                  placeholder="Search applications..."
                  (input)="appSearch = appSearchInput.value"
                />
                <p-inputicon *ngIf="!appSearch" class="search-icon"><svg lucideSearch></svg></p-inputicon>
                <p-inputicon
                  *ngIf="appSearch"
                  (click)="appSearch = ''; appSearchInput.value = ''"
                  role="button"
                  tabindex="0"
                  aria-label="Clear search"
                >
                  <svg lucideX></svg>
                </p-inputicon>
              </p-iconfield>
              <!-- Lowercase tag name: the camelCase p-splitButton selector alias was dropped by
                   PrimeNG, which silently renders nothing. -->
              <p-splitbutton
                label="Export"
                id="usage-export-split-button"
                data-cy="export-usage-csv-button"
                [model]="exportMenuItems"
                (onClick)="exportCsv()"
                [pTooltip]="exportLabel"
                tooltipStyleClass="tooltip-long"
                tooltipPosition="top"
                [attr.aria-label]="exportLabel"
              >
                <!-- The icon/dropdownIcon inputs render a class-driven <i>, which Lucide can't fill.
                     Projecting both through templates is the only way to get the svgs in. -->
                <ng-template #content>
                  <svg lucideUpload></svg>
                  Export
                </ng-template>
                <ng-template #dropdownicon>
                  <svg lucideChevronDown></svg>
                </ng-template>
              </p-splitbutton>
            </div>
          </div>
        </div>

        <section class="panel">
          <div class="table-scroll">
            <table class="usage-table" data-cy="usage-apps-table">
              <thead>
                <tr>
                  <th class="expand-col">
                    <button
                      class="expand-all-btn"
                      (click)="toggleAll()"
                      [attr.aria-label]="allExpanded ? 'Collapse all' : 'Expand all'"
                      [pTooltip]="allExpanded ? 'Collapse all' : 'Expand all'"
                      tooltipPosition="top"
                    >
                      @if (allExpanded) {
                        <svg lucideChevronsUp></svg>
                      } @else {
                        <svg lucideChevronsDown></svg>
                      }
                    </button>
                  </th>
                  <th class="date-col">Application</th>
                  <th class="num-col">Peak Billable</th>
                  <th class="bar-col"></th>
                  <th class="cloud-th">Cloud</th>
                </tr>
              </thead>
              <tbody>
                <ng-container *ngFor="let row of filteredAppBreakdown; trackBy: trackByAppId">
                  <tr
                    class="expandable-parent-row"
                    data-cy="usage-application-row"
                    [attr.data-application-id]="row.appId"
                    (click)="toggleApp(row.appId)"
                    (keydown.enter)="toggleApp(row.appId); $event.preventDefault()"
                    (keydown.space)="toggleApp(row.appId); $event.preventDefault()"
                    [attr.aria-expanded]="isAppExpanded(row.appId)"
                    role="button"
                    tabindex="0"
                  >
                    <td class="expand-col">
                      @if (isAppExpanded(row.appId)) {
                        <svg lucideChevronDown></svg>
                      } @else {
                        <svg lucideChevronRight></svg>
                      }
                    </td>
                    <td class="date-col" data-cy="usage-application-name">{{ row.appName }}</td>
                    <td class="num-col" data-cy="usage-application-billable">{{ row.billableCount | number }}</td>
                    <td class="bar-col">
                      <div class="bar-track">
                        <div class="bar-fill" [style.width.%]="(row.billableCount / maxAppBillable) * 100"></div>
                      </div>
                    </td>
                    <td class="cloud-th" data-cy="usage-application-cloud">
                      {{ row.cloud === 'AWS' ? 'AWS' : 'Azure' }}
                    </td>
                  </tr>
                  <ng-container *ngIf="isAppExpanded(row.appId)">
                    <tr
                      class="child-row"
                      data-cy="usage-resource-type-row"
                      [attr.data-resource-type]="t.resourceType"
                      *ngFor="let t of row.byType; trackBy: trackByType"
                    >
                      <td class="expand-col"></td>
                      <td class="date-col child-name" data-cy="usage-resource-type-name">{{ t.resourceType }}</td>
                      <td class="num-col" data-cy="usage-resource-type-count">{{ t.count | number }}</td>
                      <td class="bar-col">
                        <div class="bar-track">
                          <div class="bar-fill" [style.width.%]="(t.count / maxAppBillable) * 100"></div>
                        </div>
                      </td>
                      <td class="cloud-th"></td>
                    </tr>
                  </ng-container>
                </ng-container>
                <tr *ngIf="appsLoading && filteredAppBreakdown.length === 0">
                  <td colspan="5" class="muted">
                    <svg lucideLoaderCircle class="lucide-spin"></svg> Loading application detail…
                  </td>
                </tr>
                <tr *ngIf="!appsLoading && filteredAppBreakdown.length === 0">
                  <td colspan="5" class="muted" data-cy="usage-apps-empty">{{ appsEmptyMessage }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="table-footer-note" *ngIf="hiddenAppsNote" data-cy="usage-apps-hidden-note">
            {{ hiddenAppsNote }}
          </p>
        </section>
      </div>
    </div>
  </ng-container>
</div>
